/** Now — real-time company command center: who's in, where, on what, with AI pulse. */
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtWorkspaceTime } from "@/lib/wsTime";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { PersonChip } from "@/components/PersonChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Radio, MapPin, Sparkles, Loader2, RefreshCw, Zap, ListChecks, Trophy, MessageSquareHeart, Target, AlertTriangle, Wifi, Monitor, Globe, Cake, ChevronDown, ChevronRight, Users, Clock3, StickyNote, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import NowAnomaliesTab from "@/pages/app/NowAnomaliesTab";
import NowTimelineTab from "@/pages/app/NowTimelineTab";
import HotDealsWidget from "@/components/HotDealsWidget";

const STATUS_META = {
  checked_in: { label: "Checked in", cls: "bg-emerald-500/15 text-emerald-600", dot: "bg-emerald-500 animate-pulse" },
  done_for_day: { label: "Done for day", cls: "bg-sky-500/15 text-sky-600", dot: "bg-sky-500" },
  on_leave: { label: "On leave", cls: "bg-red-500/15 text-red-600", dot: "bg-red-500" },
  not_in_yet: { label: "Not in yet", cls: "bg-amber-500/15 text-amber-600", dot: "bg-amber-500" },
  no_account: { label: "No account", cls: "bg-muted text-muted-foreground", dot: "bg-foreground/30" },
};
const ANOMALY_LABEL = { new_device: "New device", new_location: "New location", new_ip: "New IP" };

export default function NowDashboard() {
  const { activeWorkspaceId: wsId } = useAuth();
  const { can } = useWorkspacePerms();
  const isAdmin = can("hr.manage");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("");
  const [action, setAction] = useState(null); // {kind, row}

  const load = useCallback(async (silent) => {
    if (!wsId) return;
    try {
      const { data: d } = await api.get(`/workspaces/${wsId}/now`);
      setData(d); setErr(null);
    } catch (e) {
      if (!silent) setErr(e?.response?.status === 403 ? "forbidden" : "error");
    }
  }, [wsId]);

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 60000);
    return () => clearInterval(iv);
  }, [load]);

  if (err === "forbidden") return (
    <>
      <PageHeader eyebrow="Operations" title="Now" description="Company command center." />
      <p className="text-sm text-muted-foreground p-6" data-testid="now-forbidden">The Now dashboard is visible to admins and managers only.</p>
    </>
  );

  const rows = (data?.rows || []).filter((r) =>
    !filter || (r.name || "").toLowerCase().includes(filter.toLowerCase()) ||
    (r.department || "").toLowerCase().includes(filter.toLowerCase()));

  return (
    <>
      <PageHeader eyebrow="Operations" title="Now — Command Center"
        description="A live picture of what's happening across the company right now." testid="now-header" />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-4">
        <SummaryStrip summary={data?.summary} asOf={data?.as_of} onRefresh={() => load()} />
        <PulsePanel wsId={wsId} />
        {/* Feb 2026 · Hot Deals widget — MCube calls with high AI followup urgency */}
        <HotDealsWidget compact testid="now-hot-deals" />

        <Tabs defaultValue="roster">
          <TabsList className="rounded-none flex-wrap h-auto justify-start" data-testid="now-subtabs">
            <TabsTrigger value="roster" className="rounded-none" data-testid="now-subtab-roster"><Users className="w-3.5 h-3.5 mr-1.5" />Live roster</TabsTrigger>
            <TabsTrigger value="anomalies" className="rounded-none" data-testid="now-subtab-anomalies"><AlertTriangle className="w-3.5 h-3.5 mr-1.5" />Anomalies</TabsTrigger>
            <TabsTrigger value="timeline" className="rounded-none" data-testid="now-subtab-timeline"><Clock3 className="w-3.5 h-3.5 mr-1.5" />Timeline</TabsTrigger>
          </TabsList>
          <TabsContent value="roster" className="mt-4">
            <RosterPane data={data} filter={filter} setFilter={setFilter} rows={rows} isAdmin={isAdmin} wsId={wsId} setAction={setAction} onChangedVisibility={() => load()} />
          </TabsContent>
          <TabsContent value="anomalies" className="mt-4"><NowAnomaliesTab /></TabsContent>
          <TabsContent value="timeline" className="mt-4"><NowTimelineTab /></TabsContent>
        </Tabs>
      </div>
      {action && action.kind === "remarks" && (
        <RemarksDialog wsId={wsId} row={action.row}
          onClose={() => setAction(null)} />
      )}
      {action && action.kind !== "remarks" && (
        <QuickActionDialog wsId={wsId} action={action}
          onClose={() => setAction(null)}
          onDone={() => { setAction(null); load(true); }} />
      )}
    </>
  );
}

function RosterPane({ data, filter, setFilter, rows, isAdmin, wsId, setAction, onChangedVisibility }) {
  const [groupByDept, setGroupByDept] = useState(false);
  const grouped = useMemo(() => {
    if (!groupByDept) return null;
    const g = new Map();
    for (const r of rows) {
      const key = r.department || "— No department";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(r);
    }
    return Array.from(g.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows, groupByDept]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name or department…"
          className="w-64 h-8 rounded-none text-xs" data-testid="now-filter" />
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={groupByDept} onChange={(e) => setGroupByDept(e.target.checked)} data-testid="now-group-by-dept" />
          Group by department
        </label>
        {isAdmin && data && <VisibilityToggle wsId={wsId} current={data.visibility} onChanged={onChangedVisibility} />}
        <span className="ml-auto text-[11px] text-muted-foreground">Auto-refreshes every 60s · {rows.length} people</span>
      </div>
      {!data ? <p className="text-sm text-muted-foreground p-6">Loading…</p> : (
        groupByDept ? (
          <div className="space-y-2" data-testid="now-grouped">
            {grouped.map(([dept, deptRows]) => (
              <DeptGroup key={dept} dept={dept} rows={deptRows} wsId={wsId} setAction={setAction} />
            ))}
            {grouped.length === 0 && <div className="border p-8 text-center text-sm text-muted-foreground">No people to show.</div>}
          </div>
        ) : (
          <div className="border overflow-x-auto" data-testid="now-table">
            <RosterTable rows={rows} wsId={wsId} setAction={setAction} />
          </div>
        )
      )}
    </div>
  );
}

function DeptGroup({ dept, rows, wsId, setAction }) {
  const [open, setOpen] = useState(true);
  const dPresent = rows.filter((r) => r.status === "checked_in").length;
  const dLeave = rows.filter((r) => r.status === "on_leave").length;
  const dAnoms = rows.filter((r) => (r.anomalies || []).length > 0).length;
  return (
    <div className="border" data-testid={`now-dept-${dept}`}>
      <button type="button" className="w-full px-3 py-2 border-b bg-muted/20 flex items-center gap-2 text-sm text-left hover:bg-muted/40" onClick={() => setOpen((v) => !v)} data-testid={`now-dept-toggle-${dept}`}>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <span className="font-semibold">{dept}</span>
        <span className="text-[11px] text-muted-foreground">{rows.length} people</span>
        <span className="ml-auto text-[11px] flex items-center gap-2">
          <span className="text-emerald-600 font-mono">{dPresent} in</span>
          <span className="text-red-600 font-mono">{dLeave} leave</span>
          {dAnoms > 0 && <span className="text-orange-600 font-mono">{dAnoms} anomaly</span>}
        </span>
      </button>
      {open && (
        <div className="overflow-x-auto">
          <RosterTable rows={rows} wsId={wsId} setAction={setAction} />
        </div>
      )}
    </div>
  );
}

function RosterTable({ rows, wsId, setAction }) {
  return (
    <table className="w-full text-sm min-w-[1080px]">
      <thead className="bg-muted/40 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
        <tr>
          <th className="px-3 py-2">Person</th>
          <th className="px-3 py-2">Shift</th>
          <th className="px-3 py-2">Status · Location</th>
          <th className="px-3 py-2">Leave</th>
          <th className="px-3 py-2">WFH</th>
          <th className="px-3 py-2">Working on</th>
          <th className="px-3 py-2">Today's plate</th>
          <th className="px-3 py-2">Projects</th>
          <th className="px-3 py-2 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => <NowRow key={r.employee_id} r={r} wsId={wsId} onAction={setAction} />)}
        {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">No one matches the filter.</td></tr>}
      </tbody>
    </table>
  );
}

function SummaryStrip({ summary, asOf, onRefresh }) {
  const items = [
    { key: "present", label: "Checked in", cls: "text-emerald-600" },
    { key: "wfh", label: "WFH", cls: "text-violet-600" },
    { key: "on_leave", label: "On leave", cls: "text-red-600" },
    { key: "not_in_yet", label: "Not in yet", cls: "text-amber-600" },
    { key: "done_for_day", label: "Done for day", cls: "text-sky-600" },
    { key: "anomalies", label: "Anomalies", cls: "text-orange-600" },
    { key: "celebrations", label: "Celebrations", cls: "text-pink-600" },
  ];
  return (
    <div className="border bg-muted/20 p-3 flex flex-wrap items-center gap-6" data-testid="now-summary">
      <span className="flex items-center gap-2 text-xs font-semibold"><Radio className="w-4 h-4 text-emerald-500 animate-pulse" />LIVE</span>
      {items.map((i) => (
        <div key={i.key} className="text-center" data-testid={`now-summary-${i.key}`}>
          <p className={`text-xl font-bold font-mono ${i.cls}`}>{summary?.[i.key] ?? "—"}</p>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{i.label}</p>
        </div>
      ))}
      <button onClick={onRefresh} className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground" data-testid="now-refresh-btn">
        <RefreshCw className="w-3.5 h-3.5" />{asOf ? `as of ${asOf.slice(11, 16)} UTC` : ""}
      </button>
    </div>
  );
}

function VisibilityToggle({ wsId, current, onChanged }) {
  const change = async (v) => {
    try {
      await api.put(`/workspaces/${wsId}/hr-settings`, { now_dashboard_visibility: v });
      toast.success("Visibility updated");
      onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  return (
    <Select value={current} onValueChange={change}>
      <SelectTrigger className="w-64 h-8 rounded-none text-xs" data-testid="now-visibility-select"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="admins_managers">Visible to admins & managers</SelectItem>
        <SelectItem value="everyone">Visible to everyone</SelectItem>
      </SelectContent>
    </Select>
  );
}

function NowRow({ r, wsId, onAction }) {
  const st = STATUS_META[r.status] || STATUS_META.not_in_yet;
  return (
    <tr className="border-t hover:bg-muted/20 align-top" data-testid={`now-row-${r.employee_id}`}>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full shrink-0 ${st.dot}`} />
          <div className="min-w-0">
            {r.user_id ? <PersonChip userId={r.user_id} name={r.name} className="font-semibold" /> : <span className="font-semibold">{r.name}</span>}
            <p className="text-[11px] text-muted-foreground truncate">{[r.designation, r.department].filter(Boolean).join(" · ") || "—"}</p>
          </div>
          {r.special_day && (
            <span className="ml-1 text-base" title={r.special_day.label} data-testid={`now-special-${r.employee_id}`}>
              {r.special_day.kind === "birthday" ? "🎂" : r.special_day.kind === "work_anniversary" ? "🎉" : "💐"}
            </span>
          )}
        </div>
      </td>
      {/* Oct 2026 · Shift column — reads the row's resolved shift
          (employee's shift_id → workspace default). Shows the window
          as a compact mono pill plus the shift's human name below. */}
      <td className="px-3 py-2.5" data-testid={`now-shift-${r.employee_id}`}>
        {r.shift ? (
          <div className="flex flex-col gap-0.5">
            <span className="inline-flex items-center font-mono text-[11px] text-foreground">
              {r.shift.start_time || "—"}–{r.shift.end_time || "—"}
            </span>
            <span className="text-[10px] text-muted-foreground truncate">
              {r.shift.name || "General"}
            </span>
          </div>
        ) : <span className="text-xs text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-2.5">
        <Badge variant="outline" className={`rounded-none border-0 text-[10px] ${st.cls}`}>{st.label}</Badge>
        {r.clock_in && <span className="ml-1.5 font-mono text-xs">{fmtWorkspaceTime(r.clock_in)}{r.clock_out ? `–${fmtWorkspaceTime(r.clock_out)}` : ""}</span>}
        {r.late_minutes > 0 && <span className="ml-1 text-[10px] text-red-500">+{r.late_minutes}m late</span>}
        {r.location && <LocationChip loc={r.location} eid={r.employee_id} />}
        {(r.anomalies || []).length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1" data-testid={`now-anomalies-${r.employee_id}`}>
            {r.anomalies.map((a) => (
              <span key={a} className="inline-flex items-center gap-1 text-[10px] bg-orange-500/15 text-orange-600 px-1.5 py-0.5">
                <AlertTriangle className="w-3 h-3" />{ANOMALY_LABEL[a] || a}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-3 py-2.5">
        <TrendHover wsId={wsId} userId={r.user_id} kind="leave" testid={`now-leave-${r.employee_id}`}>
          {r.leave ? (
            <span className={`text-xs ${r.leave.status === "approved" ? "text-red-600" : "text-amber-600"}`}>
              {r.leave.type || "Leave"}{r.leave.half_day ? " (½)" : ""} · {r.leave.status}
            </span>
          ) : <span className="text-xs text-muted-foreground">—</span>}
        </TrendHover>
      </td>
      <td className="px-3 py-2.5">
        <TrendHover wsId={wsId} userId={r.user_id} kind="wfh" testid={`now-wfh-${r.employee_id}`}>
          {r.wfh ? (
            <span className={`text-xs ${r.wfh.status === "approved" ? "text-violet-600" : "text-amber-600"}`}>WFH · {r.wfh.status}</span>
          ) : <span className="text-xs text-muted-foreground">—</span>}
        </TrendHover>
      </td>
      <td className="px-3 py-2.5 max-w-52">
        {r.working_on ? <p className="text-xs truncate" title={r.working_on.title}>▶ {r.working_on.title}</p>
          : <span className="text-xs text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-2.5">
        <TrendHover wsId={wsId} userId={r.user_id} kind="tasks" testid={`now-plate-${r.employee_id}`}>
          <span className="text-xs">
            <b className="font-mono">{r.plate.due_today}</b> due today
            {r.plate.overdue > 0 && <span className="text-red-500"> · {r.plate.overdue} overdue</span>}
            <span className="text-muted-foreground"> · {r.plate.open} open</span>
          </span>
        </TrendHover>
        {r.workload !== "ok" && (
          <span className={`block mt-0.5 text-[10px] uppercase tracking-wide ${r.workload === "overloaded" ? "text-red-500" : "text-sky-500"}`} data-testid={`now-workload-${r.employee_id}`}>
            {r.workload}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 max-w-44">
        <div className="flex flex-wrap gap-1">
          {(r.projects || []).map((p) => <span key={p.id} className="text-[10px] bg-muted px-1.5 py-0.5 truncate max-w-36" title={p.name}>{p.name}</span>)}
          {(r.projects || []).length === 0 && <span className="text-xs text-muted-foreground">—</span>}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right">
        {r.user_id && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-none h-7 px-2" data-testid={`now-actions-${r.employee_id}`}>
                <Zap className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onAction({ kind: "task", row: r })} data-testid="now-action-task"><ListChecks className="w-4 h-4 mr-2" />Assign task</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction({ kind: "goal", row: r })} data-testid="now-action-goal"><Target className="w-4 h-4 mr-2" />Assign goal</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction({ kind: "points", row: r })} data-testid="now-action-points"><Trophy className="w-4 h-4 mr-2" />Award points</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction({ kind: "feedback", row: r })} data-testid="now-action-feedback"><MessageSquareHeart className="w-4 h-4 mr-2" />Give feedback</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onAction({ kind: "remarks", row: r })} data-testid="now-action-remarks"><StickyNote className="w-4 h-4 mr-2" />Remarks</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>
    </tr>
  );
}

function LocationChip({ loc, eid }) {
  const hasCoords = loc.lat != null && loc.lng != null;
  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer hover:text-foreground" data-testid={`now-location-${eid}`}>
          <MapPin className="w-3 h-3" />
          <span className="truncate max-w-44">{loc.address || (hasCoords ? `${loc.lat.toFixed(3)}, ${loc.lng.toFixed(3)}` : loc.ip || "details")}</span>
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 rounded-none text-xs space-y-1.5" data-testid={`now-location-peek-${eid}`}>
        <p className="font-semibold flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />Check-in details</p>
        {loc.address && <p>{loc.address}</p>}
        {hasCoords && (
          <p className="text-muted-foreground">
            {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)} (±{loc.accuracy_m || "?"}m) ·{" "}
            <a className="text-primary hover:underline" target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`}>map</a>
          </p>
        )}
        <div className="grid grid-cols-2 gap-1 text-muted-foreground pt-1">
          <span className="flex items-center gap-1"><Globe className="w-3 h-3" />IP: {loc.ip || "—"}</span>
          <span className="flex items-center gap-1"><Wifi className="w-3 h-3" />{loc.platform || "web"}</span>
          <span className="flex items-center gap-1"><Monitor className="w-3 h-3" />{loc.device_type || "—"}</span>
          <span>{[loc.browser, loc.os].filter(Boolean).join(" / ") || "—"}</span>
        </div>
        {loc.timezone && <p className="text-muted-foreground">TZ: {loc.timezone}</p>}
      </HoverCardContent>
    </HoverCard>
  );
}

const trendCache = new Map();
function TrendHover({ wsId, userId, kind, children, testid }) {
  const [d, setD] = useState(null);
  const fetchTrend = async (open) => {
    if (!open || !userId) return;
    const key = `${userId}:${kind}`;
    const hit = trendCache.get(key);
    if (hit && Date.now() - hit.at < 120000) { setD(hit.data); return; }
    try {
      const { data } = await api.get(`/workspaces/${wsId}/now/trend/${userId}?kind=${kind}`);
      trendCache.set(key, { at: Date.now(), data });
      setD(data);
    } catch { setD({ error: true }); }
  };
  if (!userId) return children;
  return (
    <HoverCard openDelay={250} closeDelay={100} onOpenChange={fetchTrend}>
      <HoverCardTrigger asChild><span className="cursor-pointer" data-testid={testid}>{children}</span></HoverCardTrigger>
      <HoverCardContent className="w-72 rounded-none text-xs" data-testid={`${testid}-peek`}>
        {!d ? <p className="text-muted-foreground">Loading…</p> : d.error ? <p className="text-muted-foreground">Couldn't load.</p> : <TrendBody d={d} />}
      </HoverCardContent>
    </HoverCard>
  );
}

function TrendBody({ d }) {
  if (d.kind === "tasks") {
    const max = Math.max(1, ...(d.weeks || []).map((w) => w.completed));
    return (
      <div className="space-y-2">
        <p className="font-semibold">Task completion — last 8 weeks</p>
        <div className="flex items-end gap-1 h-12">
          {(d.weeks || []).map((w) => (
            <div key={w.week} className="flex-1 bg-emerald-500/70" title={`${w.week}: ${w.completed}`} style={{ height: `${(w.completed / max) * 100}%`, minHeight: 3 }} />
          ))}
          {(d.weeks || []).length === 0 && <p className="text-muted-foreground">No completions yet.</p>}
        </div>
        <p className="text-muted-foreground"><b className="font-mono text-foreground">{d.completed_8w}</b> done in 8w · {d.open} open · <span className={d.overdue ? "text-red-500" : ""}>{d.overdue} overdue</span></p>
      </div>
    );
  }
  const months = d.months || [];
  const max = Math.max(1, ...months.map((m) => m.days));
  return (
    <div className="space-y-2">
      <p className="font-semibold">{d.kind === "leave" ? "Leave" : "WFH"} — last 6 months</p>
      <div className="flex items-end gap-1 h-12">
        {months.map((m) => (
          <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5">
            <div className={`w-full ${d.kind === "leave" ? "bg-red-500/70" : "bg-violet-500/70"}`} style={{ height: `${(m.days / max) * 40}px`, minHeight: 3 }} title={`${m.month}: ${m.days}d`} />
            <span className="text-[9px] text-muted-foreground">{m.month.slice(5)}</span>
          </div>
        ))}
        {months.length === 0 && <p className="text-muted-foreground">None in the last 6 months.</p>}
      </div>
      <p className="text-muted-foreground"><b className="font-mono text-foreground">{d.total_days_180d}</b> day(s) in 180 days</p>
      {(d.recent || []).slice(0, 3).map((r, i) => (
        <p key={i} className="text-muted-foreground">
          {d.kind === "leave" ? `${r.type || "Leave"} ${r.from}→${r.to}` : r.date} · <span className={r.status === "approved" ? "text-emerald-600" : r.status === "pending" ? "text-amber-600" : "text-red-500"}>{r.status}</span>
        </p>
      ))}
    </div>
  );
}

function PulsePanel({ wsId }) {
  const [pulse, setPulse] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/now/pulse`).then((r) => { if (r.data.status === "done") setPulse(r.data); }).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [wsId]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data: job } = await api.post(`/workspaces/${wsId}/now/pulse`);
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/workspaces/${wsId}/now/pulse?job_id=${job.job_id}`);
          if (st.status === "done") { clearInterval(pollRef.current); setPulse(st); setBusy(false); }
          if (st.status === "failed") { clearInterval(pollRef.current); toast.error(st.error || "Pulse failed"); setBusy(false); }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to start pulse")); setBusy(false); }
  };

  return (
    <div className="border border-violet-500/30 bg-violet-500/5 p-3" data-testid="now-pulse-panel">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-violet-600" />
        <p className="text-sm font-semibold flex-1">AI Company Pulse</p>
        {pulse?.finished_at && <span className="text-[10px] text-muted-foreground">generated {pulse.finished_at.slice(11, 16)} UTC</span>}
        <Button size="sm" variant="outline" className="rounded-none h-7" onClick={generate} disabled={busy} data-testid="now-pulse-generate">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          {busy ? "Analyzing…" : pulse ? "Regenerate" : "Generate pulse"}
        </Button>
      </div>
      {pulse?.text && <pre className="mt-2 text-xs whitespace-pre-wrap font-sans" data-testid="now-pulse-text">{pulse.text.replace(/\*\*/g, "")}</pre>}
      {!pulse && !busy && <p className="mt-1 text-xs text-muted-foreground">Claude summarizes attendance, workload risks, anomalies and celebrations into a daily operations brief.</p>}
    </div>
  );
}

function QuickActionDialog({ wsId, action, onClose, onDone }) {
  const { kind, row } = action;
  const [f, setF] = useState({ title: "", due_date: "", priority: "medium", message: "", sentiment: "positive", points: 10, reason: "", end_date: "" });
  const [busy, setBusy] = useState(false);
  const titles = { task: `Assign task to ${row.name}`, goal: `Assign goal to ${row.name}`, points: `Award points to ${row.name}`, feedback: `Feedback for ${row.name}` };

  const submit = async () => {
    setBusy(true);
    try {
      if (kind === "task") {
        if (!f.title.trim()) { toast.error("Title required"); setBusy(false); return; }
        await api.post(`/workspaces/${wsId}/now/quick/task`, { user_id: row.user_id, title: f.title, due_date: f.due_date || null, priority: f.priority });
        toast.success("Task assigned");
      } else if (kind === "goal") {
        if (!f.title.trim()) { toast.error("Title required"); setBusy(false); return; }
        await api.post(`/workspaces/${wsId}/goals`, { title: f.title, owner_type: "individual", owner_user_id: row.user_id, horizon: "quarterly", mode: "okr", progress_mode: "manual", manual_progress_pct: 0, end_date: f.end_date || null });
        toast.success("Goal created");
      } else if (kind === "points") {
        if (!f.reason.trim()) { toast.error("Reason required"); setBusy(false); return; }
        await api.post(`/workspaces/${wsId}/points-award`, { user_id: row.user_id, points: Number(f.points) || 0, reason: f.reason });
        toast.success("Points awarded");
      } else if (kind === "feedback") {
        if (!f.message.trim()) { toast.error("Message required"); setBusy(false); return; }
        await api.post(`/workspaces/${wsId}/now/quick/feedback`, { user_id: row.user_id, message: f.message, sentiment: f.sentiment });
        toast.success("Feedback sent");
      }
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none sm:max-w-md" data-testid="now-quick-dialog">
        <DialogHeader><DialogTitle className="text-base">{titles[kind]}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {(kind === "task" || kind === "goal") && (
            <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={kind === "task" ? "Task title" : "Goal title"} className="rounded-none" data-testid="quick-title" />
          )}
          {kind === "task" && (
            <div className="flex gap-2">
              <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="rounded-none" data-testid="quick-due" />
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}>
                <SelectTrigger className="rounded-none w-32" data-testid="quick-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {kind === "goal" && <Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="rounded-none" data-testid="quick-goal-end" />}
          {kind === "points" && (
            <>
              <Input type="number" value={f.points} onChange={(e) => setF({ ...f, points: e.target.value })} className="rounded-none" data-testid="quick-points" />
              <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Reason" className="rounded-none" data-testid="quick-reason" />
            </>
          )}
          {kind === "feedback" && (
            <>
              <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Your feedback…" className="rounded-none min-h-24" data-testid="quick-message" />
              <Select value={f.sentiment} onValueChange={(v) => setF({ ...f, sentiment: v })}>
                <SelectTrigger className="rounded-none" data-testid="quick-sentiment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="positive">🌟 Positive / appreciation</SelectItem>
                  <SelectItem value="constructive">💡 Constructive</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
          <Button onClick={submit} disabled={busy} className="rounded-none w-full" data-testid="quick-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Cake className="hidden" />}
            {busy ? "Saving…" : "Confirm"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── R268g · PM/HR remarks tabbed dialog ──────────────────────────
export function RemarksDialog({ wsId, row, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none sm:max-w-lg max-h-[85vh] overflow-y-auto"
                        data-testid="now-remarks-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <StickyNote className="w-4 h-4" /> Remarks · {row.name}
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            PM tab shows performance notes · HR tab shows people notes. Only permitted users can see & write each tab.
          </DialogDescription>
        </DialogHeader>
        <RemarksPanel wsId={wsId} userId={row.user_id} />
      </DialogContent>
    </Dialog>
  );
}

// R268fff · Feb 2026 — Extracted the remarks UI (PM / HR / Compare
// subtabs) into a plain panel so it can be embedded elsewhere — the
// new Employee Overview popup now hosts it as a subtab, and any
// future surface (Team roster row, Now widget…) can reuse the same
// panel without a dialog wrapper.
export function RemarksPanel({ wsId, userId }) {
  const [caps, setCaps] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pm");
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  // R268i — HR compare view
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  // R268L — @mention picker state
  const [members, setMembers] = useState([]);
  const [mentionMatches, setMentionMatches] = useState([]);
  const [mentionIdx, setMentionIdx] = useState(0);
  const textareaRef = useRef(null);
  useEffect(() => {
    // Cache workspace members once for the @-mention popover
    api.get(`/workspaces/${wsId}/members`)
       .then(({ data }) => setMembers(Array.isArray(data) ? data : []))
       .catch(() => setMembers([]));
  }, [wsId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get(`/workspaces/${wsId}/employees/${userId}/remarks`);
      setCaps(r.data.capabilities || null);
      setItems(r.data.items || []);
      // Auto-pick the first visible tab
      const c = r.data.capabilities || {};
      if (!c.can_view_pm && c.can_view_hr) setActiveTab("hr");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load remarks")); }
    finally { setLoading(false); }
  }, [wsId, userId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!draft.trim()) { toast.error("Write something first"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/employees/${userId}/remarks`,
                                   { kind: activeTab, text: draft.trim() });
      setItems((prev) => [r.data, ...prev]);
      setDraft("");
      toast.success(`${activeTab.toUpperCase()} remark saved`);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const deleteRemark = async (rid) => {
    try {
      await api.post(`/workspaces/${wsId}/employees/${userId}/remarks/${rid}/delete`, {});
      setItems((prev) => prev.filter((x) => x.id !== rid));
      toast.success("Removed");
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // R268i · load PM vs HR compare when the tab is opened
  const loadCompare = useCallback(async () => {
    setCompareLoading(true);
    try {
      const r = await api.get(
        `/workspaces/${wsId}/employees/${userId}/remarks/compare`,
        { params: { date_from: dateFrom, date_to: dateTo } },
      );
      setCompareData(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Compare failed")); }
    finally { setCompareLoading(false); }
  }, [wsId, userId, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab === "compare" && caps
        && caps.can_view_pm && caps.can_view_hr) {
      loadCompare();
    }
  }, [activeTab, caps, loadCompare]);

  const canViewAny = caps && (caps.can_view_pm || caps.can_view_hr);
  const canWriteActive = caps && ((activeTab === "pm" && caps.can_write_pm)
                                     || (activeTab === "hr" && caps.can_write_hr));

  const filtered = items.filter((r) => r.kind === activeTab);

  return (
    <>
        {loading ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            <Loader2 className="w-4 h-4 mx-auto animate-spin" />
          </div>
        ) : !canViewAny ? (
          <div className="py-8 text-center text-xs text-muted-foreground"
                  data-testid="now-remarks-forbidden">
            You don't have permission to view remarks.
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
            <TabsList className="rounded-none">
              {caps.can_view_pm && (
                <TabsTrigger value="pm" className="rounded-none"
                                data-testid="now-remarks-tab-pm">
                  PM remarks
                  {caps.can_write_pm ? null
                    : <span className="ml-1 text-[9px] uppercase text-muted-foreground">(read-only)</span>}
                </TabsTrigger>
              )}
              {caps.can_view_hr && (
                <TabsTrigger value="hr" className="rounded-none"
                                data-testid="now-remarks-tab-hr">
                  HR remarks
                  {caps.can_write_hr ? null
                    : <span className="ml-1 text-[9px] uppercase text-muted-foreground">(read-only)</span>}
                </TabsTrigger>
              )}
              {caps.can_view_pm && caps.can_view_hr && (
                <TabsTrigger value="compare" className="rounded-none"
                                data-testid="now-remarks-tab-compare">
                  Compare
                </TabsTrigger>
              )}
            </TabsList>

            {["pm", "hr"].map((k) =>
              caps[`can_view_${k}`] && (
                <TabsContent key={k} value={k} className="space-y-3 mt-3">
                  {canWriteActive && activeTab === k && (
                    <div className="space-y-2 relative" data-testid={`now-remarks-composer-${k}`}>
                      <Textarea
                        ref={textareaRef}
                        value={draft}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraft(v);
                          // Detect an in-progress @token
                          const cursor = e.target.selectionStart || v.length;
                          const upto = v.slice(0, cursor);
                          const m = upto.match(/@([A-Za-z0-9._\- ]{0,30})$/);
                          if (m && members.length) {
                            const q = m[1].toLowerCase().trim();
                            const hits = (members || []).slice(0, 30).filter((mem) => {
                              const nm = ((mem.user && mem.user.name) || mem.name || "").toLowerCase();
                              const em = ((mem.user && mem.user.email) || mem.email || "").toLowerCase();
                              return !q || nm.includes(q) || em.includes(q);
                            }).slice(0, 6);
                            setMentionMatches(hits);
                            setMentionIdx(0);
                          } else {
                            setMentionMatches([]);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (!mentionMatches.length) return;
                          if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx((i) => (i + 1) % mentionMatches.length); }
                          else if (e.key === "ArrowUp") { e.preventDefault(); setMentionIdx((i) => (i - 1 + mentionMatches.length) % mentionMatches.length); }
                          else if (e.key === "Enter" || e.key === "Tab") {
                            e.preventDefault();
                            const pick = mentionMatches[mentionIdx];
                            if (!pick) return;
                            const nm = (pick.user && pick.user.name) || pick.name || (pick.user && pick.user.email) || "";
                            const handle = nm.split(" ")[0] || nm;
                            // Replace the partial @token
                            const el = textareaRef.current;
                            const cursor = el ? (el.selectionStart || draft.length) : draft.length;
                            const before = draft.slice(0, cursor).replace(/@([A-Za-z0-9._\- ]{0,30})$/, `@${handle} `);
                            const after = draft.slice(cursor);
                            setDraft(before + after);
                            setMentionMatches([]);
                            requestAnimationFrame(() => { if (el) { el.selectionStart = el.selectionEnd = before.length; el.focus(); } });
                          } else if (e.key === "Escape") { setMentionMatches([]); }
                        }}
                        placeholder={`Add a ${k.toUpperCase()} remark…  (type @ to mention a teammate)`}
                        className="rounded-none min-h-20"
                        data-testid={`now-remarks-input-${k}`} />

                      {mentionMatches.length > 0 && (
                        <div className="absolute left-0 top-full mt-1 z-40 w-64 max-h-52 overflow-y-auto border border-border bg-background shadow-md"
                                data-testid="now-remarks-mention-popover">
                          {mentionMatches.map((mem, i) => {
                            const nm = (mem.user && mem.user.name) || mem.name || "";
                            const em = (mem.user && mem.user.email) || mem.email || "";
                            return (
                              <button
                                key={(mem.user && mem.user.id) || mem.user_id || i}
                                onMouseDown={(e) => { e.preventDefault();
                                  setMentionIdx(i);
                                  // Trigger the same enter-picking path
                                  const el = textareaRef.current;
                                  const cursor = el ? (el.selectionStart || draft.length) : draft.length;
                                  const handle = (nm.split(" ")[0] || nm || em);
                                  const before = draft.slice(0, cursor).replace(/@([A-Za-z0-9._\- ]{0,30})$/, `@${handle} `);
                                  const after = draft.slice(cursor);
                                  setDraft(before + after);
                                  setMentionMatches([]);
                                  requestAnimationFrame(() => { if (el) { el.selectionStart = el.selectionEnd = before.length; el.focus(); } });
                                }}
                                className={"w-full text-left px-2 py-1.5 text-[11px] hover:bg-primary/10 " + (i === mentionIdx ? "bg-primary/10" : "")}>
                                <div className="font-semibold">{nm || em}</div>
                                {nm && em && <div className="text-[10px] text-muted-foreground truncate">{em}</div>}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <div className="flex justify-end">
                        <Button size="sm" onClick={submit} disabled={saving || !draft.trim()}
                                  className="rounded-none h-8"
                                  data-testid={`now-remarks-save-${k}`}>
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                                    : <StickyNote className="w-3.5 h-3.5 mr-1" />}
                          Save
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2" data-testid={`now-remarks-list-${k}`}>
                    {filtered.length === 0 ? (
                      <div className="text-[11px] text-muted-foreground text-center py-4">
                        No {k.toUpperCase()} remarks yet.
                      </div>
                    ) : filtered.map((r) => (
                      <div key={r.id}
                              className="border border-border p-2 text-xs space-y-1"
                              data-testid={`now-remarks-item-${r.id}`}>
                        <div className="whitespace-pre-wrap">{r.text}</div>
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                          <span>{r.author_name} · {new Date(r.created_at).toLocaleString()}</span>
                          {canWriteActive && (
                            <button onClick={() => deleteRemark(r.id)}
                                      className="hover:text-rose-600"
                                      data-testid={`now-remarks-delete-${r.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )
            )}
            {caps.can_view_pm && caps.can_view_hr && (
              <TabsContent value="compare" className="space-y-3 mt-3"
                            data-testid="now-remarks-compare-panel">
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-muted-foreground">From</span>
                  <input type="date" value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="border border-border h-7 px-1 rounded-none bg-background"
                            data-testid="now-remarks-compare-from" />
                  <span className="text-muted-foreground">to</span>
                  <input type="date" value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="border border-border h-7 px-1 rounded-none bg-background"
                            data-testid="now-remarks-compare-to" />
                  <Button size="sm" variant="outline" className="h-7 ml-auto rounded-none"
                            onClick={loadCompare}
                            data-testid="now-remarks-compare-refresh">
                    <RefreshCw className="w-3 h-3 mr-1" />Refresh
                  </Button>
                </div>
                {compareLoading ? (
                  <div className="py-6 text-center text-xs text-muted-foreground">
                    <Loader2 className="w-4 h-4 mx-auto animate-spin" />
                  </div>
                ) : !compareData ? (
                  <div className="py-6 text-center text-xs text-muted-foreground">
                    Click Refresh to load the compare view.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "pm", label: "PM remarks", color: "border-l-indigo-500",
                        rows: compareData.pm || [] },
                      { key: "hr", label: "HR remarks", color: "border-l-emerald-500",
                        rows: compareData.hr || [] },
                    ].map((col) => (
                      <div key={col.key} className="space-y-2"
                              data-testid={`now-remarks-compare-col-${col.key}`}>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                          {col.label} · {col.rows.length}
                        </div>
                        {col.rows.length === 0 ? (
                          <div className="text-[11px] text-muted-foreground italic text-center py-4">
                            No {col.key.toUpperCase()} remarks in this range.
                          </div>
                        ) : col.rows.map((r) => (
                          <div key={r.id}
                                  className={`border border-border border-l-2 ${col.color} p-2 text-[11px] space-y-1 bg-background`}>
                            <div className="whitespace-pre-wrap">{r.text}</div>
                            <div className="text-[9px] text-muted-foreground">
                              {r.author_name} · {new Date(r.created_at).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            )}
          </Tabs>
        )}
    </>
  );
}

